import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, CheckCircle, MessageCircle, ArrowRight,
  IndianRupee, Star, FileText
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const LifeInsurancePage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Life Insurance Plans in India | Term & Whole Life Insurance | AGO Innovations',
    description:
      'Get expert life insurance advice in India. AGO Innovations helps you compare term insurance, whole life, and ULIP plans from LIC, HDFC, ICICI, and more. Free consultation.',
    keywords:
      'life insurance India, term insurance plans, life insurance consultant India, best term insurance, LIC policy, HDFC life insurance, compare life insurance plans, insurance advisor near me',
    canonical: 'https://agoinnovations.in/finance/life-insurance',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'Life Insurance Advisory — AGO Innovations',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description: 'Expert life insurance advisory in India. Compare term insurance, whole life, and ULIP plans.',
      areaServed: 'IN',
      serviceType: 'Life Insurance Advisory',
    },
  });

  const plans = [
    { name: 'Term Insurance', desc: 'Pure life cover at the lowest premium. Best for most people. ₹1 crore cover from ₹8,000/year.', badge: 'Most Popular', color: 'bg-blue-700' },
    { name: 'Whole Life / Endowment', desc: 'Combines insurance + savings. Guaranteed returns at maturity. Best for conservative investors.', badge: 'Safe Option', color: 'bg-emerald-700' },
    { name: 'ULIPs', desc: 'Market-linked insurance. Combines equity investments with life cover. Best for high-risk investors.', badge: 'Market-Linked', color: 'bg-orange-600' },
    { name: 'Child Plans', desc: 'Secure your child\'s education and future milestones. Pays out at key ages regardless of what happens.', badge: 'For Families', color: 'bg-purple-700' },
  ];

  const benefits = [
    { icon: <IndianRupee className="w-5 h-5" />, title: 'Tax Benefits', desc: 'Premiums up to ₹1.5 lakh deductible under Section 80C. Death benefit fully tax-free under Section 10(10D).' },
    { icon: <Shield className="w-5 h-5" />, title: 'Family Protection', desc: 'Lump sum payout ensures your family can cover loans, education, and living expenses.' },
    { icon: <Star className="w-5 h-5" />, title: 'Low Premiums Early', desc: 'A 25-year-old pays 3–4x less than a 45-year-old for the same cover. Starting early saves significantly.' },
    { icon: <FileText className="w-5 h-5" />, title: 'Easy Claim Process', desc: 'AGO advisors help guide nominees through the claim process smoothly when it matters most.' },
  ];

  const faqs = [
    { q: 'How much life insurance cover do I need?', a: 'Rule of thumb: 10–15x your annual income. A ₹5 lakh/year earner should have at least ₹50–75 lakh cover, adjusted for loans and dependents.' },
    { q: 'What is the difference between term and whole life insurance?', a: 'Term insurance is pure protection at low cost — it pays only on death. Whole life combines savings + insurance at higher premiums. For most people, term is the better choice.' },
    { q: 'Which life insurance company is best in India?', a: 'LIC, HDFC Life, ICICI Prudential, SBI Life, and Max Life are among the top insurers. The best plan depends on your age, budget, and goals — our advisors help you compare.' },
    { q: 'Can I buy life insurance online?', a: 'Yes. Most insurers offer online purchase. AGO Innovations helps you compare plans and guides you through the purchase process.' },
    { q: 'What is the right age to buy life insurance?', a: 'The younger you are, the lower your premium. Ideally buy in your 20s or early 30s. But any time you have dependents is the right time.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">
            🛡️ Finance — Life Insurance
          </span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">
            Life Insurance Plans
            <span className="block text-yellow-400">in India</span>
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Protect your family's financial future with the right life insurance. Compare term plans, whole life, and ULIP options — expert guidance, zero pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/contact')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              Get Free Advice <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20life%20insurance%20advice."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-blue-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Free Consultation</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Compare All Major Insurers</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> No Hidden Charges</span>
          </div>
        </motion.div>
      </section>

      {/* PLAN TYPES */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Types of Life Insurance Plans</h2>
            <p className="text-gray-500 text-lg">Choose the right plan based on your needs, budget, and goals.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {plans.map((plan, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Shield className="w-6 h-6" />
                  </div>
                  <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${plan.color}`}>{plan.badge}</span>
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{plan.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Why Life Insurance is Essential</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {benefits.map((b, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="flex gap-5 bg-blue-50 rounded-xl p-5 border border-blue-100"
              >
                <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">{b.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{b.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Life Insurance FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-gray-100"
              >
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-800 to-indigo-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black mb-4">Find the Right Life Insurance Today</h2>
          <p className="text-blue-200 mb-8">Our advisors will help you compare, choose, and purchase the right plan — for free.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/contact')} className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full transition-colors">
              Book Free Consultation
            </button>
            <button onClick={() => navigate('/finance')} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-colors">
              View All Finance Services
            </button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default LifeInsurancePage;
